import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "providers/form";
import GenericButton from "widgets/controls/buttons/generic";
import i18next from "i18next";

// Schema definition for the form
const AccountAddSchema = z.object({
  name: z.string().min(1, { message: i18next.t("zod.error.required") }),
});

export type AccountAddFormType = z.infer<typeof AccountAddSchema>;

// Props for the form
type AccountAddFormProps = {
  submit: (data: AccountAddFormType) => void;
  onClose?: () => void;
  defaultValues?: AccountAddFormType;
  values?: AccountAddFormType;
  loading?: boolean;
};

const AccountAddForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
}: AccountAddFormProps) => {
  const { t } = useTranslation();

  const form = useForm<AccountAddFormType>({
    resolver: zodResolver(AccountAddSchema),
    defaultValues,
    values,
  });

  const { formState, handleSubmit, reset } = form;
  const { errors } = formState;

  const onSubmit = async (formData: AccountAddFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleClose = () => {
    reset();
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className={`mb-4 grid grid-cols-2 gap-2`}>
          <FormField
            control={form.control}
            name="name"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.account.name")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.account.name")}
                    id="snr"
                    type="text"
                    disabled={loading}
                    state={errors.name ? "error" : undefined}
                    {...field}
                    color="secondary"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex w-full flex-row gap-2">
          <GenericButton
            text={t("button.action.clear")}
            loading={loading || false}
            type="button"
            size="md"
            variant="outline"
            onClick={handleClose}
            color="secondary"
          />
          <GenericButton
            text={t("button.action.create")}
            loading={loading || false}
            type="submit"
            size="md"
          />
        </div>
      </form>
    </Form>
  );
};

export default AccountAddForm;
